import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, DatabaseZap, Info, ServerCog, XCircle } from "lucide-react";
import { cn } from "@/lib/cn.js";
import { claves, webchatsApi } from "@/lib/webchat/api.js";
import type { ErrorInterpretado } from "@/lib/webchat/errores.js";

type TonoAviso = "info" | "aviso" | "error";

const ESTILO: Record<TonoAviso, string> = {
  info: "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-100",
  aviso: "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
  error: "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-100",
};

const ICONO: Record<TonoAviso, typeof Info> = { info: Info, aviso: AlertTriangle, error: XCircle };

/** Un recuadro de aviso con ícono, título y texto. Los de error se anuncian (role=alert). */
export function Aviso({
  tono,
  titulo,
  children,
  className,
  icono,
}: Readonly<{ tono: TonoAviso; titulo: ReactNode; children?: ReactNode; className?: string; icono?: typeof Info }>) {
  const Icono = icono ?? ICONO[tono];
  return (
    <div role={tono === "error" ? "alert" : undefined} className={cn("rounded-md border px-4 py-3 text-sm flex gap-3", ESTILO[tono], className)}>
      <Icono size={18} className="shrink-0 mt-0.5" aria-hidden="true" />
      <div className="space-y-1 min-w-0">
        <p className="font-medium">{titulo}</p>
        {children && <div className="text-[13px] leading-relaxed">{children}</div>}
      </div>
    </div>
  );
}

/** El error de una acción del módulo, con la explicación de los casos especiales. */
export function AvisoError({ error, className }: Readonly<{ error: ErrorInterpretado | null; className?: string }>) {
  if (!error) return null;
  if (error.tipo === "grant") {
    return (
      <Aviso tono="error" titulo={error.titulo} icono={DatabaseZap} className={className}>
        <p>{error.mensaje}</p>
        <p className="mt-1">
          La cuenta <span className="mono">bo_app</span> no tiene los permisos que el módulo necesita en el core, así que esta acción no
          se hizo (y si era un cambio, no se guardó nada). Lo resuelve quien administra la base corriendo la parte A de{" "}
          <span className="mono">sql/2026-10-14-webchats-grants.sql</span> y <span className="mono">sql/2026-11-04-webchats-otp-grants.sql</span> en el writer.
        </p>
      </Aviso>
    );
  }
  if (error.tipo === "config") {
    return (
      <Aviso tono="error" titulo={error.titulo} icono={ServerCog} className={className}>
        <p>{error.mensaje}</p>
        {error.faltan.length > 0 && (
          <p className="mt-1">
            Variables que faltan en bo-api: <span className="mono">{error.faltan.join(", ")}</span>.
          </p>
        )}
      </Aviso>
    );
  }
  return (
    <Aviso tono={error.tipo === "conflicto" || error.tipo === "limite" ? "aviso" : "error"} titulo={error.titulo} className={className}>
      <p>{error.mensaje}</p>
      {error.codigo && error.codigo !== "RED" && <p className="mt-1 text-xs opacity-75 mono">Código: {error.codigo}</p>}
    </Aviso>
  );
}

/**
 * Lo que todavía falta para que el módulo funcione completo, leído de
 * /webchats/capacidades: configuración de Cloudflare y S3, DDL de la fase 1 y
 * el release del webchat (sin él, las imágenes nuevas aún no se ven).
 */
export function AvisoCapacidades({ imagenes = false, className }: Readonly<{ imagenes?: boolean; className?: string }>) {
  const { data } = useQuery({ queryKey: claves.capacidades, queryFn: webchatsApi.capacidades, staleTime: 60_000 });
  if (!data) return null;
  const avisos: { clave: string; tono: TonoAviso; titulo: string; texto: ReactNode }[] = [];
  if (!data.cloudflare.configurado) {
    avisos.push({
      clave: "cf",
      tono: "error",
      titulo: "Cloudflare no está configurado en bo-api",
      texto: (
        <>
          No se pueden crear ni dar de baja webchats. Falta: <span className="mono">{data.cloudflare.faltan.join(", ")}</span>.
        </>
      ),
    });
  }
  if (imagenes && !data.s3.configurado) {
    avisos.push({
      clave: "s3",
      tono: "aviso",
      titulo: "Las imágenes no se pueden subir todavía",
      texto: (
        <>
          Falta configurar S3 en bo-api: <span className="mono">{data.s3.faltan.join(", ")}</span>.
        </>
      ),
    });
  }
  if (imagenes && !data.ddl.marcaS3) {
    avisos.push({
      clave: "ddl",
      tono: "aviso",
      titulo: "Las imágenes esperan un cambio de base",
      texto: "El DDL de la fase 1 del webchat (02-marca-s3.sql) todavía no está aplicado: subir imágenes responde «DDL pendiente».",
    });
  }
  if (imagenes && data.ddl.marcaS3 && !data.ddl.prefijoAlineado) {
    avisos.push({
      clave: "prefijo",
      tono: "error",
      titulo: "La base y bo-api no usan la misma carpeta de S3",
      texto: (
        <>
          El CHECK <span className="mono">CK_WEXTR_ORIGEN</span> del DDL de imágenes exige otro prefijo que el de bo-api: no se sube nada
          hasta alinearlos (avisá a quien administra la base).
        </>
      ),
    });
  }
  if (imagenes && data.ddl.marcaS3 && !data.release.flag) {
    avisos.push({
      clave: "release",
      tono: "aviso",
      titulo: "Las imágenes nuevas aún no se ven en el webchat",
      texto:
        "Se guardan en S3 y quedan en el tema, pero el webchat que está en producción todavía no las sirve: se ven cuando se despliegue el release fase 1.",
    });
  }
  if (!avisos.length) return null;
  return (
    <div className={cn("space-y-2", className)}>
      {avisos.map((a) => (
        <Aviso key={a.clave} tono={a.tono} titulo={a.titulo}>
          {a.texto}
        </Aviso>
      ))}
    </div>
  );
}
